import { api } from "../config/variables";
import { trackClarityEvent } from "../utils/clarity";
import { readCookie } from "../utils/functions";
import { isMobile } from "../utils/mobileDetection";

import SERVICE_NAMES from "./constants/serviceConstants";
import moEngageQueue, { EVENT_PRIORITY } from "./moengage/MoEngageQueue";
import { shouldSendToService } from "./registry/eventRegistry";

/**
 * Analytics Service Manager
 * Provides unified interface for all analytics services
 */
class AnalyticsManager {
	constructor() {
		this.services = {
			postHog: null,
			moEngage: null,
			gtm: null,
		};
		this.isInitialized = false;
		this.initPromise = null;
	}

	/**
	 * Initialize all analytics services.
	 *
	 * Idempotent, and returns the same promise on every call so events fired
	 * before the service imports resolve can chain off it instead of being lost.
	 *
	 * @returns {Promise<void>}
	 */
	initialize() {
		if (!this.initPromise) {
			this.initPromise = this.runInitialization();
		}
		return this.initPromise;
	}

	/**
	 * Load and start every enabled service. Call initialize() instead — this is
	 * the one-shot body it guards.
	 */
	async runInitialization() {
		try {
			const importPromises = [];

			if (api.postHog?.isEnabled) {
				importPromises.push(
					import("./services/postHog").then(({ default: postHogService }) => {
						this.services.postHog = postHogService;
						postHogService.initialize();
						return postHogService;
					})
				);
			}

			if (api.moEngage?.isEnabled) {
				importPromises.push(
					import("./services/moEngage")
						.then(async ({ default: moEngageService }) => {
							this.services.moEngage = moEngageService;
							// Wire the queue before initialising the SDK so any events
							// fired during init are captured and flushed once ready.
							moEngageQueue.init(moEngageService);
							await moEngageService.initialize();
							return moEngageService;
						})
						.catch((error) => {
							console.error("Failed to load MoEngage service:", error);
							// The service failed to load so the queue will never get a
							// service reference and can never flush. Clear persisted events
							// to prevent unbounded localStorage growth across reloads.
							moEngageQueue.clearQueue();
							return null;
						})
				);
			}

			if (api.gtm?.isEnabled) {
				importPromises.push(
					import("./services/gtm")
						.then(({ default: gtmService }) => {
							this.services.gtm = gtmService;
							gtmService.initialize();
							return gtmService;
						})
						.catch((error) => {
							console.error("Failed to load GTM service:", error);
							return null;
						})
				);
			}

			await Promise.all(importPromises);
			this.isInitialized = true;
			// eslint-disable-next-line no-console
			console.log("Analytics Manager initialized successfully");
		} catch (error) {
			console.error("Failed to initialize Analytics Manager:", error);
		}
	}

	/**
	 * Track event across all configured analytics providers.
	 *
	 * MoEngage is routed through the event queue so events fired before the
	 * SDK finishes loading are buffered and flushed automatically — the caller
	 * does not need to wait for initialization.
	 *
	 * PostHog and GTM still require full initialization before they will fire.
	 *
	 * @param {string} eventName - Name of the event
	 * @param {Object} properties - Event properties
	 * @param {Object} options - Additional options passed to each SDK
	 * @param {number} [priority] - Queue priority for MoEngage (EVENT_PRIORITY.HIGH | NORMAL | LOW)
	 */
	trackEvent(
		eventName,
		properties = {},
		options = {},
		priority = EVENT_PRIORITY.NORMAL
	) {
		if (!eventName || typeof eventName !== "string") {
			return;
		}

		const clientCookie = readCookie("client", true);
		const env = api.currentEnv;
		const isStaff = Boolean(clientCookie?.is_staff);
		const commonProperties = {
			source: isMobile() ? "mweb" : "web",
			email: clientCookie?.email || "",
			company_id: clientCookie?.client_poc?.client?.sr_id || "",
			phone_number: clientCookie?.contact_no || "",
		};

		// Shared env eligibility — must be staging/production (or dev with debug on).
		// Returns false for unknown envs (undefined, "test", "local", etc.) so events
		// never fire in environments that weren't explicitly configured.
		const isEnabledForEnv = (debugFlag) => {
			if (env === "dev") {
				return Boolean(debugFlag);
			}
			if (env === "staging" || env === "production") {
				return !isStaff;
			}
			return false;
		};

		const moEngageEnabled =
			api.moEngage?.isEnabled &&
			shouldSendToService(eventName, SERVICE_NAMES.MOENGAGE) &&
			isEnabledForEnv(api.moEngage?.debug);

		if (moEngageEnabled) {
			const moEngageProperties = {
				...commonProperties,
				bu_name: "Cargo",
				is_first: "TRUE",
				...properties,
			};
			moEngageQueue.track(eventName, moEngageProperties, options, priority);
		}
		const context = { env, isStaff, commonProperties };

		if (this.isInitialized) {
			this.dispatchToSdks(eventName, properties, options, context);
			return;
		}

		// PostHog and GTM load through dynamic imports, so an event fired during
		// app boot used to hit an uninitialised manager and be dropped outright.
		// Replay it once initialization settles instead. The original event time
		// rides along in `properties`, not `options`: PostHog reads its third
		// argument as capture options where `timestamp` must be a Date, while
		// GTM spreads properties into the event params. As a property it reaches
		// gtag and the backend mirror without corrupting PostHog's event time.
		if (this.initPromise) {
			const occurredAt = new Date().toISOString();
			this.initPromise
				.then(() =>
					this.dispatchToSdks(
						eventName,
						{ timestamp: occurredAt, ...properties },
						options,
						context
					)
				)
				.catch(() => {});
		}
	}

	/**
	 * Send one event to the SDKs that are loaded, ready, and registered for it.
	 *
	 * @param {string} eventName
	 * @param {Object} properties
	 * @param {Object} options
	 * @param {{env: string, isStaff: boolean, commonProperties: Object}} context
	 *   Captured at call time so a replayed event carries the state it was fired
	 *   with rather than whatever is true once initialization finishes.
	 */
	dispatchToSdks(eventName, properties, options, context) {
		const { env, isStaff, commonProperties } = context;

		// Split out of isServiceReady so sinks that need the policy but not the
		// SDK-readiness half can share it. Staff exclusion in particular has to
		// apply everywhere: a sink that skips it reports internal traffic the
		// others drop, and the two datasets stop reconciling on volume.
		const isAllowedForEnv = (debugFlag) => {
			if (env === "dev") {
				return Boolean(debugFlag);
			}
			if (env === "staging" || env === "production") {
				return !isStaff;
			}
			return false;
		};

		const isServiceReady = (serviceKey, serviceName, debugFlag) => {
			const service = this.services[serviceKey];
			if (
				!service ||
				!service.isReady() ||
				!shouldSendToService(eventName, serviceName)
			) {
				return false;
			}
			return isAllowedForEnv(debugFlag);
		};

		if (isServiceReady("postHog", SERVICE_NAMES.POSTHOG, api.postHog?.debug)) {
			this.services.postHog.trackEvent(
				eventName,
				{ ...commonProperties, ...properties },
				options
			);
		}

		if (isServiceReady("gtm", SERVICE_NAMES.GOOGLEANALYTICS, api.gtm?.debug)) {
			this.services.gtm.trackEvent(
				eventName,
				{ ...commonProperties, bu_name: "Cargo", ...properties },
				options
			);
		}

		// Clarity is not a lazily-imported SDK like the three above — the snippet
		// loads from index.html and trackClarityEvent no-ops when it is absent, so
		// it takes the registry and policy checks but not the SDK-readiness one.
		//
		// The policy check is not optional: without it the Clarity funnel counts
		// staff sessions that GA4 excludes, and the two cannot be reconciled.
		// `false` for the dev debug flag is deliberate — Clarity has no such flag,
		// and the snippet is production-host-gated in index.html, so dev would
		// no-op anyway.
		//
		// Properties are dropped on purpose: clarity("event", …) is name-only, and
		// funnel segmentation comes from the session tags in setClarityCustomKeys.
		if (
			shouldSendToService(eventName, SERVICE_NAMES.CLARITY) &&
			isAllowedForEnv(false)
		) {
			trackClarityEvent(eventName);
		}
	}

	/**
	 * Get service status
	 * @returns {Object} Status of all services
	 */
	getServiceStatus() {
		return {
			postHog: this.services.postHog ? this.services.postHog.isReady() : false,
			moEngage: this.services.moEngage
				? this.services.moEngage.isReady()
				: false,
			gtm: this.services.gtm ? this.services.gtm.isReady() : false,
		};
	}

	/**
	 * Get detailed service loading status for debugging
	 * @returns {Object} Detailed status of all services
	 */
	getDetailedServiceStatus() {
		return {
			isInitialized: this.isInitialized,
			services: {
				postHog: {
					loaded: Boolean(this.services.postHog),
					ready: this.services.postHog
						? this.services.postHog.isReady()
						: false,
					service: this.services.postHog,
				},
				moEngage: {
					loaded: Boolean(this.services.moEngage),
					ready: this.services.moEngage
						? this.services.moEngage.isReady()
						: false,
					service: this.services.moEngage,
				},
				gtm: {
					loaded: Boolean(this.services.gtm),
					ready: this.services.gtm ? this.services.gtm.isReady() : false,
					service: this.services.gtm,
				},
			},
		};
	}
}

const analyticsManager = new AnalyticsManager();
export default analyticsManager;
